import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { PaperProvider, Text } from "react-native-paper";
import { SafeAreaProvider } from "react-native-safe-area-context";

import { QuestionsChapter } from "@/components/section/QuestionsChapter";
import type { Question, ValidationError } from "@/lib/api";
import { evaluateVisibility, type QuestionWithRules } from "@/lib/branching";
import {
    isDrawn,
    parseInbound,
    type FormPreviewOutbound,
    type FormPreviewQuestion
} from "@/lib/form-preview";
import { colors, fonts, paperTheme } from "@/lib/theme";

/**
 * The admin Form Viewer's device pane.
 *
 * Mounted by `app/_layout.tsx` INSTEAD of the app when the page is loaded with
 * `?formPreview=1`, before AuthProvider / WebSocketProvider / push registration
 * and before the retired-web gate — a preview holds no session, opens no
 * socket, and is not a nurse using the PWA.
 *
 * It renders the real `QuestionsChapter`, which is what a nurse's section
 * screen renders, so what the admin sees is what the device draws: the same
 * controls, the same branching engine, the same render order.
 *
 * Nothing here writes anywhere. Answers live in component state for the life of
 * the page and are posted back to the host purely so the catalog can highlight
 * what changed.
 */

/** Empty validation state — a preview has no visit, so it has no errors. */
const NO_ERRORS = new Map<string | null, ValidationError[]>();
const NO_ORPHANS: ValidationError[] = [];
const NO_DISMISSED = new Set<number>();
const NO_REQUIRED = new Set<string>();

function post(message: FormPreviewOutbound): void {
    try {
        globalThis.parent?.postMessage(message, "*");
    } catch {
        // Not embedded, or a browser that refuses the post. The preview still
        // works standalone — it just cannot report back.
    }
}

export function FormPreviewHost() {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [sectionName, setSectionName] = useState("");
    const [sectionId, setSectionId] = useState("preview");
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [loaded, setLoaded] = useState(false);

    const scrollRef = useRef<ScrollView>(null);
    const questionYPositions = useRef(new Map<string, number>());
    // Reported alongside the next state message so the host knows which row to
    // flash. Held in a ref because it describes an event, not render state.
    const lastChange = useRef<{ questionId: string; value: string } | null>(null);

    useEffect(() => {
        function onMessage(event: MessageEvent) {
            const msg = parseInbound(event.data);
            if (!msg) {
                return;
            }
            if (msg.type === "form-preview:load") {
                // The wire shape is structurally the mobile Question; the cast
                // is the boundary between an untrusted postMessage payload and
                // the app's own type.
                setQuestions(msg.questions as unknown as Question[]);
                setSectionName(msg.sectionName);
                setSectionId(msg.sectionId);
                setAnswers(msg.answers ?? {});
                setLoaded(true);
                lastChange.current = null;
                scrollRef.current?.scrollTo({ y: 0, animated: false });
                return;
            }
            if (msg.type === "form-preview:reset") {
                setAnswers({});
                lastChange.current = null;
                return;
            }
            // scroll-to: only questions actually drawn have a recorded y.
            const y = questionYPositions.current.get(msg.questionId);
            if (y != null) {
                scrollRef.current?.scrollTo({ y: Math.max(0, y - 80), animated: true });
            }
        }

        globalThis.addEventListener?.("message", onMessage);
        post({ type: "form-preview:ready" });
        return () => globalThis.removeEventListener?.("message", onMessage);
    }, []);

    // Recomputed from the same engine the chapter renders with, so the host's
    // highlight can never disagree with what is on screen.
    const visibleIds = useMemo(() => {
        const roots = questions.filter((q) => q.visibleOnMobile !== false && !q.parentQuestionId);
        const withRules: QuestionWithRules[] = roots.map((q) => ({
            id: q.id,
            branchingRules: q.branchingRules ?? []
        }));
        return evaluateVisibility(withRules, answers);
    }, [questions, answers]);

    useEffect(() => {
        if (!loaded) {
            return;
        }
        const visible = questions.filter((q) => visibleIds.has(q.id));
        post({
            type: "form-preview:state",
            answers,
            visibleIds: visible.map((q) => q.id),
            renderedIds: visible
                .filter((q) => isDrawn(q as unknown as FormPreviewQuestion))
                .map((q) => q.id),
            lastChange: lastChange.current
        });
    }, [answers, visibleIds, questions, loaded]);

    const handleChange = useCallback((questionId: string, value: string) => {
        lastChange.current = { questionId, value };
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }, []);

    const noop = useCallback(() => {}, []);

    return (
        <SafeAreaProvider>
            <PaperProvider theme={paperTheme}>
                <View style={styles.screen}>
                    {loaded ? (
                        <ScrollView
                            ref={scrollRef}
                            style={styles.scroll}
                            contentContainerStyle={styles.scrollContent}
                            keyboardShouldPersistTaps="handled"
                        >
                            <Text style={styles.sectionName}>{sectionName}</Text>
                            <QuestionsChapter
                                questions={questions}
                                answers={answers}
                                onChange={handleChange}
                                readOnly={false}
                                visitId="preview"
                                sectionId={sectionId}
                                errorsByQuestion={NO_ERRORS}
                                orphanErrors={NO_ORPHANS}
                                dismissedLocalIds={NO_DISMISSED}
                                onAddNarrative={noop}
                                onAddDiagnosis={noop}
                                onAcknowledge={noop}
                                requiredErrors={NO_REQUIRED}
                                questionYPositions={questionYPositions}
                            />
                        </ScrollView>
                    ) : (
                        <View style={styles.waiting}>
                            <Text style={styles.waitingText}>Waiting for a section…</Text>
                        </View>
                    )}
                </View>
            </PaperProvider>
        </SafeAreaProvider>
    );
}

const styles = StyleSheet.create({
    screen: { flex: 1, backgroundColor: colors.background },
    scroll: { flex: 1 },
    scrollContent: { paddingBottom: 80 },
    sectionName: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: colors.textTertiary,
        paddingHorizontal: 20,
        paddingTop: 18
    },
    waiting: { flex: 1, alignItems: "center", justifyContent: "center" },
    waitingText: { fontFamily: fonts.regular, fontSize: 14, color: colors.textTertiary }
});
